import React, { useState, useEffect } from 'react';
import { Save, Loader2, Image, Type, Palette, Share2, Instagram, Youtube, Twitter, GripVertical, Plus, X, Eye, ShoppingBag, BookOpen, Users, Gift, Calendar, Link as LinkIcon, Sparkles, Star, TrendingUp, Package } from 'lucide-react';
import { getStorefrontSettings, updateStorefrontSettings, updateSectionOrder } from '../firebase/storefront';
import { getAllProducts } from '../firebase/products';
import { ImageUpload } from './ImageUpload';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FeatureGuide, HelpTooltip, InfoBanner } from './HelpTooltip';

// Sortable Section Item
function SortableSection({ section, products, onEdit, onDelete, onAddProduct }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sectionProducts = products.filter(p => section.productIds?.includes(p.id));

  const getSectionIcon = (type) => {
    switch (type) {
      case 'products': return <ShoppingBag size={20} />;
      case 'courses': return <BookOpen size={20} />;
      case 'memberships': return <Users size={20} />;
      case 'freebies': return <Gift size={20} />;
      case 'bookings': return <Calendar size={20} />;
      case 'custom': return <LinkIcon size={20} />;
      default: return <Package size={20} />;
    }
  };

  const getSectionColor = (type) => {
    switch (type) {
      case 'products': return 'from-blue-500 to-blue-600';
      case 'courses': return 'from-purple-500 to-purple-600';
      case 'memberships': return 'from-pink-500 to-pink-600';
      case 'freebies': return 'from-green-500 to-green-600';
      case 'bookings': return 'from-orange-500 to-orange-600';
      case 'custom': return 'from-gray-500 to-gray-600';
      default: return 'from-[#c5a059] to-[#b08e4d]';
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 mb-4 overflow-hidden ${
        isDragging 
          ? 'border-[#c5a059] shadow-2xl scale-105' 
          : 'border-gray-200 hover:border-[#c5a059] hover:shadow-xl'
      }`}
    >
      {/* Header with gradient accent */}
      <div className={`bg-gradient-to-r ${getSectionColor(section.type)} p-1`}>
        <div className="bg-white rounded-t-xl">
          <div className="flex items-center justify-between p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
              <button
                {...attributes}
                {...listeners}
                className={`cursor-grab active:cursor-grabbing p-2 rounded-lg transition-all flex-shrink-0 ${
                  isDragging 
                    ? 'bg-[#c5a059] text-white' 
                    : 'text-gray-400 hover:text-[#c5a059] hover:bg-gray-50'
                }`}
              >
                <GripVertical size={20} />
              </button>
              
              <div className={`p-2.5 sm:p-3 rounded-xl bg-gradient-to-br ${getSectionColor(section.type)} text-white flex-shrink-0 shadow-md`}>
                {getSectionIcon(section.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-gray-800 text-base sm:text-lg truncate">
                    {section.title || 'Untitled Section'}
                  </h3>
                  {sectionProducts.length > 0 && (
                    <span className="px-2 py-0.5 bg-[#c5a059]/10 text-[#c5a059] rounded-full text-xs font-semibold flex-shrink-0">
                      {sectionProducts.length}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <span className="text-gray-500 capitalize">{section.type}</span>
                  <span className="text-gray-300">•</span>
                  <span className="text-gray-500">
                    {sectionProducts.length} product{sectionProducts.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
              <button
                onClick={() => onEdit(section)}
                className="p-2 sm:p-2.5 text-gray-400 hover:text-[#c5a059] hover:bg-[#c5a059]/10 rounded-lg transition-all duration-200"
                title="Edit section"
              >
                <Type size={18} />
              </button>
              <button
                onClick={() => onDelete(section.id)}
                className="p-2 sm:p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                title="Delete section"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Products List */}
      <div className="px-4 sm:px-6 pb-4 sm:pb-6">
        {sectionProducts.length > 0 ? (
          <div className="space-y-2 mt-4">
            {sectionProducts.map((product, idx) => (
              <div 
                key={product.id} 
                className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-[#c5a059]/30 hover:shadow-md transition-all duration-200 group"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                {product.thumbnail ? (
                  <img 
                    src={product.thumbnail} 
                    alt={product.title} 
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover border-2 border-white shadow-sm flex-shrink-0" 
                  />
                ) : (
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-gradient-to-br from-[#c5a059] to-[#d48c95] flex items-center justify-center text-white flex-shrink-0 shadow-sm">
                    <Package size={16} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm sm:text-base font-medium text-gray-800 truncate">{product.title}</p>
                  {product.price !== undefined && (
                    <p className="text-xs text-gray-500">
                      {(() => {
                        const price = parseFloat(product.price) || 0;
                        return isNaN(price) || price === 0 ? 'Free' : `£${price.toFixed(2)}`;
                      })()}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => onAddProduct(section.id, product.id, true)}
                  className="p-1.5 sm:p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100 flex-shrink-0"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 py-8 text-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
            <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400 font-medium">No products in this section</p>
            <p className="text-xs text-gray-400 mt-1">Click "Edit" to add products</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Section Editor Modal
function SectionEditorModal({ section, products, onSave, onClose }) {
  const [title, setTitle] = useState(section?.title || '');
  const [type, setType] = useState(section?.type || 'products');
  const [selectedProductIds, setSelectedProductIds] = useState(section?.productIds || []);

  const handleSave = () => {
    onSave({
      ...section,
      title,
      type,
      productIds: selectedProductIds
    });
  };

  const toggleProduct = (productId) => {
    setSelectedProductIds(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const getFilteredProducts = () => {
    if (!products || products.length === 0) return [];
    
    // Map section types to product types
    const typeMapping = {
      'courses': 'online_course',
      'memberships': 'membership',
      'freebies': 'freebie',
      'products': 'digital_download',
      'bookings': 'coaching_session',
      'custom': null // Show all for custom
    };
    
    const targetProductType = typeMapping[type];
    
    // If no mapping or custom type, show all products
    if (!targetProductType) {
      return products;
    }
    
    // Filter by product type
    return products.filter(product => product.type === targetProductType);
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-hidden"
      onClick={onClose}
      style={{ touchAction: 'none' }}
    >
      <div 
        className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-2xl h-[90vh] sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '90vh', touchAction: 'auto' }}
      >
        {/* Header - Sticky */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 sm:px-6 sm:py-6 flex items-center justify-between z-10 flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Edit Section</h2>
          <button 
            onClick={onClose} 
            className="p-2 text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-6 space-y-4 sm:space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 text-base rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] min-h-[44px]"
              placeholder="e.g. Featured Products, Courses, Free Resources"
              style={{ fontSize: '16px' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-4 py-3 text-base rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] min-h-[44px] bg-white"
              style={{ fontSize: '16px' }}
            >
              <option value="products">Products</option>
              <option value="courses">Courses</option>
              <option value="memberships">Memberships</option>
              <option value="freebies">Free Resources</option>
              <option value="bookings">Bookings</option>
              <option value="custom">Custom Link</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Products in Section</label>
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4 max-h-[40vh] sm:max-h-64 overflow-y-auto overscroll-contain">
              {getFilteredProducts().length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No products available</p>
              ) : (
                <div className="space-y-2">
                  {getFilteredProducts().map(product => (
                    <label
                      key={product.id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer touch-manipulation min-h-[60px]"
                    >
                      <input
                        type="checkbox"
                        checked={selectedProductIds.includes(product.id)}
                        onChange={() => toggleProduct(product.id)}
                        className="w-5 h-5 sm:w-4 sm:h-4 text-[#c5a059] rounded focus:ring-[#c5a059] flex-shrink-0 touch-manipulation"
                        style={{ minWidth: '20px', minHeight: '20px' }}
                      />
                      {product.thumbnail && (
                        <img 
                          src={product.thumbnail} 
                          alt={product.title} 
                          className="w-12 h-12 sm:w-10 sm:h-10 rounded object-cover flex-shrink-0" 
                        />
                      )}
                      <span className="flex-1 text-sm sm:text-base text-gray-700 min-w-0 truncate">{product.title}</span>
                      <span className="text-xs sm:text-sm text-gray-500 flex-shrink-0">
                        {product.price === 0 ? 'Free' : `£${product.price}`}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer - Sticky */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-4 sm:px-6 sm:py-6 flex justify-end gap-3 flex-shrink-0 shadow-lg sm:shadow-none">
          <button
            onClick={onClose}
            className="px-5 py-3 text-gray-600 hover:text-gray-800 transition min-h-[48px] text-base font-medium touch-manipulation"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-3 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition min-h-[48px] text-base font-medium touch-manipulation"
          >
            Save Section
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminStorefrontTab({ showToast }) {
  const [settings, setSettings] = useState({
    profilePicture: '',
    displayName: '',
    bio: '',
    // Hero Section
    heroBadge: 'Cultural Rewiring & Self-Love',
    heroTagline: 'Return to your',
    heroTaglineHighlight: 'truest self.',
    heroDescription: 'Helping women heal, set boundaries, and rise higher through feminine energy, nervous system regulation, and cultural conditioning healing.',
    // About Section
    aboutTitle: 'Who I Am',
    aboutQuote: 'Fabiha is a self-love and cultural rewiring coach with 8+ years of experience in mindset work, manifestation, and inner child healing.',
    aboutContent1: 'I blend clinical knowledge (as an NHS pharmacist), mind-body principles, and spiritual alignment practices to help women reconnect with themselves.',
    aboutContent2: 'My mission is to help you break cultural conditioning and build a life rooted in confidence and peace.',
    aboutCredentials: ['Trained in meditation facilitation', 'LOA & mindset experience', 'South Asian background', 'Trauma-informed approach', 'Heart-centred coaching style'],
    aboutPhilosophyTitle: 'My Philosophy',
    aboutPhilosophy: 'Healing isn\'t about becoming someone new. It\'s about unlearning who you were told to be, so you can be who you truly are.',
    aboutPhilosophyAuthor: '— Fabiha',
    // Programme Section
    programmeTitle: '12-Week 1:1 Coaching Programme',
    programmeDescription: 'A structured progression for feminine energy, cultural healing, and personalized nervous system tools.',
    programmeFeatures: ['Weekly 1:1 Zoom calls', 'Tailored worksheets', 'Journaling prompts', 'Voice note/DM support', 'Personalised meditation', 'Feminine energy tools', 'Nervous system regulation', 'Cultural conditioning healing'],
    programmePrice: 555,
    programmeOriginalPrice: 1111,
    programmePriceNote: 'Full price from early 2026',
    programmeBadge: 'Intro Price',
    programmePaymentNote: 'Payment plans available upon request.',
    // Freebie Section
    freebieTitle: 'Free Guided Meditation:',
    freebieTitleHighlight: '"Return to Self"',
    freebieDescription: 'Reconnect with your inner peace. Enter your details below to receive this powerful audio experience directly to your inbox.',
    freebieDisclaimer: 'You\'ll also be added to my newsletter for weekly tips on self-love & alignment. Unsubscribe anytime.',
    socialLinks: {
      instagram: '',
      tiktok: '',
      youtube: '',
      twitter: ''
    },
    colors: {
      primary: '#c5a059',
      secondary: '#d48c95',
      background: '#fdfbf7',
      text: '#1f2937'
    },
    sections: []
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsData, productsData] = await Promise.all([
        getStorefrontSettings(),
        getAllProducts()
      ]);
      
      if (settingsData) {
        setSettings(prev => ({
          ...prev,
          ...settingsData,
          socialLinks: { ...prev.socialLinks, ...settingsData.socialLinks },
          colors: { ...prev.colors, ...settingsData.colors },
          sections: settingsData.sections || []
        }));
      }
      
      setProducts(productsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateStorefrontSettings(settings);
      if (result.success) {
        showToast('Storefront settings saved', 'success');
      } else {
        showToast('Error saving settings: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error saving settings: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSocialChange = (network, value) => {
    setSettings(prev => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [network]: value }
    }));
  };

  const handleColorChange = (colorKey, value) => {
    setSettings(prev => ({
      ...prev,
      colors: { ...prev.colors, [colorKey]: value }
    }));
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = settings.sections.findIndex(s => s.id === active.id);
      const newIndex = settings.sections.findIndex(s => s.id === over.id);
      const newSections = arrayMove(settings.sections, oldIndex, newIndex);
      
      // Update order numbers
      const reorderedSections = newSections.map((section, index) => ({
        ...section,
        order: index
      }));
      
      setSettings(prev => ({ ...prev, sections: reorderedSections }));
      
      // Save to Firestore
      try {
        await updateSectionOrder(reorderedSections);
        showToast('Section order updated', 'success');
      } catch (error) {
        showToast('Error updating order', 'error');
      }
    }
  };

  const handleAddSection = () => {
    const newSection = {
      id: `section_${Date.now()}`,
      title: 'New Section',
      type: 'products',
      productIds: [],
      order: settings.sections.length
    };
    setSettings(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    setEditingSection(newSection);
  };

  const handleEditSection = (section) => {
    setEditingSection(section);
  };

  const handleSaveSection = (updatedSection) => {
    setSettings(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === updatedSection.id ? updatedSection : s
      )
    }));
    setEditingSection(null);
    showToast('Section updated', 'success');
  };

  const handleDeleteSection = async (sectionId) => {
    if (!confirm('Delete this section? This action cannot be undone.')) return;
    
    const updatedSections = settings.sections.filter(s => s.id !== sectionId);
    const updatedSettings = {
      ...settings,
      sections: updatedSections
    };
    
    // Update local state immediately for responsive UI
    setSettings(updatedSettings);
    
    // Save to Firestore
    try {
      const result = await updateStorefrontSettings(updatedSettings);
      if (result.success) {
        showToast('Section deleted successfully', 'success');
      } else {
        // Revert on error
        setSettings(settings);
        showToast('Error deleting section: ' + result.error, 'error');
      }
    } catch (error) {
      // Revert on error
      setSettings(settings);
      showToast('Error deleting section: ' + error.message, 'error');
    }
  };

  const handleAddProductToSection = (sectionId, productId, remove = false) => {
    setSettings(prev => ({
      ...prev,
      sections: prev.sections.map(section => 
        section.id === sectionId
          ? {
              ...section,
              productIds: remove
                ? section.productIds.filter(id => id !== productId)
                : [...(section.productIds || []), productId]
            }
          : section
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-[#c5a059]" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold text-gray-800">Storefront Builder</h2>
          <HelpTooltip
            title="Storefront Builder"
            content="Build your link-in-bio style storefront where customers can browse and purchase your products. Customize your profile, organize products into sections, and create a beautiful branded experience. Your storefront is accessible at /storefront and can be shared as your main link."
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <Eye size={18} className="mr-2" />
            {previewMode ? 'Edit Mode' : 'Preview'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-6 py-2.5 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover-lift button-press"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      <FeatureGuide
        title="Storefront Builder Guide"
        description="Your storefront is a separate link-in-bio style page (/storefront) where customers discover and purchase your products. This is different from your landing page (homepage). Build sections to organize products, customize colors to match your brand, and create a seamless shopping experience."
        steps={[
          "Upload your profile picture (recommended: square, 400x400px or larger)",
          "Add your display name and bio to introduce yourself",
          "Connect your social media accounts (Instagram, TikTok, YouTube, Twitter)",
          "Customize colors to match your brand (primary, secondary, background)",
          "Create sections to organize products (e.g., 'Featured Products', 'Courses', 'Free Resources')",
          "Add products to each section by clicking 'Edit' on a section",
          "Drag sections to reorder them (top section appears first)",
          "Click 'Preview' to see how it looks to customers",
          "Click 'Save Changes' to publish your storefront"
        ]}
        tips={[
          "Keep your bio concise but compelling (2-3 sentences)",
          "Use high-quality images for your profile picture",
          "Organize products into logical sections (by type, price, or audience)",
          "Test your storefront on mobile devices - most customers browse on phones",
          "Update your storefront regularly to keep it fresh",
          "Share your storefront URL (/storefront) as your main link"
        ]}
      />

      <InfoBanner type="info" title="Storefront vs Landing Page">
        <p className="text-sm mb-2">
          <strong>Storefront</strong> (this tab): Your link-in-bio page at <strong>{typeof window !== 'undefined' ? window.location.origin : ''}/storefront</strong>. This is for showcasing and selling products. Share this link on social media.
        </p>
        <p className="text-sm">
          <strong>Landing Page</strong> (separate tab): Your main marketing homepage with Hero, About, Programme, Freebie, and Booking sections. This is your main website URL.
        </p>
      </InfoBanner>

      {previewMode ? (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">Preview mode - showing how your storefront will look</p>
            <a
              href="/storefront"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition"
            >
              <Eye size={18} className="mr-2" />
              Open Storefront in New Tab
            </a>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Profile Section */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-yellow-50 rounded-lg">
                  <Image className="text-[#c5a059]" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-800">Profile Information</h3>
                  <p className="text-sm text-gray-500">Your public profile that appears at the top of your storefront</p>
                </div>
                <HelpTooltip
                  title="Profile Information"
                  content="This information appears at the top of your storefront. Your profile picture, name, and bio are the first things customers see. Make it welcoming and authentic. Your profile picture should be clear and professional (square format works best)."
                />
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                  <input
                    type="text"
                    value={settings.displayName || ''}
                    onChange={(e) => handleChange('displayName', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                    placeholder="e.g. Fabiha Zaman"
                  />
                </div>
                
                <div>
                  <ImageUpload 
                    value={settings.profilePicture}
                    onChange={(url) => handleChange('profilePicture', url)}
                    label="Profile Picture"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                  <textarea
                    rows={4}
                    value={settings.bio || ''}
                    onChange={(e) => handleChange('bio', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all resize-none"
                    placeholder="Tell your story..."
                  />
                </div>
              </div>
            </div>

            {/* Appearance Section */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <Palette className="text-purple-600" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-800">Appearance</h3>
                  <p className="text-sm text-gray-500">Customize colors to match your brand identity</p>
                </div>
                <HelpTooltip
                  title="Color Customization"
                  content="Choose colors that match your brand. Primary color is used for buttons and links. Secondary color is for accents. Background color sets the overall page background. Use color pickers or enter hex codes. Changes apply immediately in preview."
                />
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={settings.colors?.primary || '#c5a059'}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-500 uppercase">{settings.colors?.primary}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={settings.colors?.secondary || '#d48c95'}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-500 uppercase">{settings.colors?.secondary}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={settings.colors?.background || '#fdfbf7'}
                      onChange={(e) => handleColorChange('background', e.target.value)}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-500 uppercase">{settings.colors?.background}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Social Links Section */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Share2 className="text-blue-600" size={24} />
              </div>
              <h3 className="text-lg font-medium text-gray-800">Social Links</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                <div className="relative">
                  <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={settings.socialLinks?.instagram || ''}
                    onChange={(e) => handleSocialChange('instagram', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                    placeholder="@username or URL"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">TikTok</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 font-bold text-xs">TK</span>
                  <input
                    type="text"
                    value={settings.socialLinks?.tiktok || ''}
                    onChange={(e) => handleSocialChange('tiktok', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                    placeholder="@username or URL"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">YouTube</label>
                <div className="relative">
                  <Youtube className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={settings.socialLinks?.youtube || ''}
                    onChange={(e) => handleSocialChange('youtube', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                    placeholder="Channel URL"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Twitter / X</label>
                <div className="relative">
                  <Twitter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={settings.socialLinks?.twitter || ''}
                    onChange={(e) => handleSocialChange('twitter', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all"
                    placeholder="@username or URL"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sections Builder */}
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-md">
                  <ShoppingBag className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
                    Storefront Sections
                    <Sparkles className="text-[#c5a059] hidden sm:inline" size={20} />
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Drag to reorder • Click to edit • Organize your products beautifully</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <HelpTooltip
                  title="Storefront Sections"
                  content="Sections organize your products on your storefront. Each section can contain multiple products. Drag the grip icon (⋮⋮) to reorder sections. Click 'Edit' to add/remove products from a section. Sections appear in order from top to bottom on your storefront."
                />
                <button
                  onClick={handleAddSection}
                  className="flex items-center px-5 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-[#c5a059] to-[#b08e4d] hover:from-[#b08e4d] hover:to-[#9d7a3f] text-white rounded-xl transition-all duration-200 shadow-md hover:shadow-lg font-semibold min-h-[48px]"
                >
                  <Plus size={20} className="mr-2" />
                  <span className="hidden sm:inline">Add Section</span>
                  <span className="sm:hidden">Add</span>
                </button>
              </div>
            </div>

            {settings.sections.length === 0 ? (
              <div className="text-center py-16 sm:py-20 border-2 border-dashed border-gray-300 rounded-2xl bg-gradient-to-br from-gray-50 to-white">
                <div className="inline-flex p-4 bg-gradient-to-br from-[#c5a059] to-[#d48c95] rounded-2xl mb-6 shadow-lg">
                  <ShoppingBag className="w-12 h-12 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">Start Building Your Storefront</h4>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  Create sections to organize your products beautifully. Each section can showcase different types of offerings.
                </p>
                <button
                  onClick={handleAddSection}
                  className="px-8 py-3 bg-gradient-to-r from-[#c5a059] to-[#b08e4d] hover:from-[#b08e4d] hover:to-[#9d7a3f] text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl font-semibold min-h-[48px]"
                >
                  <Plus size={20} className="inline mr-2" />
                  Create Your First Section
                </button>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={settings.sections.map(s => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {settings.sections.map((section) => (
                    <SortableSection
                      key={section.id}
                      section={section}
                      products={products}
                      onEdit={handleEditSection}
                      onDelete={handleDeleteSection}
                      onAddProduct={handleAddProductToSection}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </>
      )}

      {editingSection && (
        <SectionEditorModal
          section={editingSection}
          products={products}
          onSave={handleSaveSection}
          onClose={() => setEditingSection(null)}
        />
      )}
    </div>
  );
}
